import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { id: "home", label: "Home", href: "#home", tid: NAV.linkHome },
  { id: "about", label: "About", href: "#about", tid: "nav-link-about" },
  { id: "services", label: "Services", href: "#services", tid: NAV.linkServices },
  { id: "gallery", label: "Gallery", href: "#gallery", tid: NAV.linkGallery },
  { id: "contact", label: "Contact", href: "#contact", tid: NAV.linkContact },
];

const OMNI_BADGE = "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/b35qdpuq_Omni%20Certified%20Installer%20Logo%20%281%29.png";
const YG_LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/poi36fgm_younggunslighting%20logo.png";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,border,padding] duration-500 ${
        scrolled ? "nav-glass border-b border-white/5 py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="container-x grid grid-cols-[1fr_auto_1fr] items-center gap-6">
        <a
          href="#home"
          data-testid={NAV.logo}
          onClick={(e) => { e.preventDefault(); scrollTo("#home"); }}
          className="flex items-center gap-3 group justify-self-center"
        >
          <img
            src={YG_LOGO}
            alt="Young Guns Lighting Solutions"
            className="h-48 md:h-64 lg:h-72 xl:h-80 w-auto object-contain drop-shadow-[0_0_45px_rgba(0,229,255,0.6)] transition-transform duration-500 group-hover:scale-[1.02]"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.tid}
              onClick={() => scrollTo(l.href)}
              className="font-mono-accent text-[11px] tracking-[0.28em] uppercase text-white/70 hover:text-[color:var(--primary)] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4 justify-self-end">
          <a
            href="#services"
            onClick={(e) => { e.preventDefault(); scrollTo("#services"); }}
            className="hidden md:flex items-center gap-3 pr-2 group"
            aria-label="Omni RGB Certified Installer"
          >
            <div className="rounded-full bg-white p-1.5 shadow-[0_0_28px_rgba(0,229,255,0.5)] transition-transform duration-500 group-hover:scale-105">
              <img src={OMNI_BADGE} alt="Omni RGB Certified Installer" className="h-14 w-14 md:h-16 md:w-16 object-contain" />
            </div>
            <div className="leading-tight hidden xl:block">
              <div className="text-[10px] tracking-[0.28em] text-white/50 font-mono-accent uppercase">Omni RGB</div>
              <div className="text-[13px] tracking-[0.15em] text-white uppercase font-mono-accent font-medium">Certified Installer</div>
            </div>
          </a>

          <button
            data-testid={NAV.quoteBtn}
            onClick={() => scrollTo("#contact")}
            className="hidden md:inline-flex items-center gap-2 px-6 py-3 bg-[color:var(--primary)] text-black font-mono-accent text-[11px] tracking-[0.22em] uppercase hover:bg-white hover:glow-blue transition-colors btn-sharp"
          >
            Get a Quote
          </button>

          <button
            data-testid={NAV.mobileToggle}
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden nav-glass border-t border-white/5"
          >
            <div className="container-x py-6 flex flex-col gap-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`${l.tid}-mobile`}
                  onClick={() => scrollTo(l.href)}
                  className="text-left font-mono-accent text-xs tracking-[0.25em] uppercase text-white/80 py-2"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid={`${NAV.quoteBtn}-mobile`}
                onClick={() => scrollTo("#contact")}
                className="mt-2 px-5 py-3 bg-[color:var(--primary)] text-black font-mono-accent text-xs tracking-[0.2em] uppercase btn-sharp"
              >
                Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
